import { ref, computed } from "vue";
import * as EmailValidator from "email-validator";

const isValidUrl = (str) => {
  try {
    const url = new URL(str);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return false;
    }

    const host = url.hostname.toLowerCase();
    const parts = host.split(".");
    if (parts.length < 2) {
      return false;
    }
    const tld = parts[parts.length - 1];
    return /^[a-z]{2,}$/i.test(tld);
  } catch(err) {
    return false;
  }
};

const getEmailValidationText = (email) => {
  let validationText = "";

  if (email === "") {
    validationText = "";
  } else if (!email.includes("@")) {
    validationText = "Email must include '@' symbol";
  } else if (!EmailValidator.validate(email)) {
    validationText = "Invalid email format";
  }

  return validationText;
};

const isInternationalPhoneNumber = (phoneNumber) => {
  const trimmed = phoneNumber.trim();
  const formatRegex = /^\+[\d\s\-().]{7,20}$/;
  if (!formatRegex.test(trimmed)) return false;
  const digitsOnly = trimmed.replace(/\D/g, "");
  return digitsOnly.length >= 10 && digitsOnly.length <= 15;
};

const sendForm = (body) =>
  fetch(`${process.env.NEXT_PUBLIC_API_URL}/form`, {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then((res) => (res.status === 200 ? res.json() : Promise.reject()))
    .then((data) => data.success || Promise.reject());

export function useContactUsForm() {
  // Fields
  const name = ref("");
  const nameError = ref("");

  const companyName = ref("");

  const email = ref("");
  const emailError = ref("");

  const phoneNumber = ref("");
  const phoneNumberError = ref("");

  const website = ref("");
  const websiteError = ref("");

  const contactChannel = ref("");

  const message = ref("");
  const messageError = ref("");

  // Validators
  const validateName = () => {
    nameError.value = name.value.length ? "" : "This field is required.";
  };

  const validateEmail = () => {
    if (!email.value.length) {
      emailError.value = "This field is required.";
    } else {
      emailError.value = getEmailValidationText(email.value);
    }
  };

  const validatePhoneNumber = () => {
    if (!phoneNumber.value) {
      phoneNumberError.value = "This field is required.";
    } else {
      phoneNumberError.value = isInternationalPhoneNumber(phoneNumber.value)
        ? ""
        : "Must be an International Format (e.g., +371 12345678)";
    }
  };

  const handlePhoneChange = (value) => {
    if (!value) return;
    let newValue = value;
    if (value[0] !== "+" && value.length > 0) {
      newValue = `+${value}`;
    }
    phoneNumber.value = newValue;
  };

  const validateWebsite = () => {
    websiteError.value =
      !website.value.length || isValidUrl(website.value)
        ? ""
        : "Please enter a valid web address (e.g., https://example.com)";
  };

  const validateMessage = () => {
    messageError.value =
      message.value.length < 15
        ? "Minimum length not met—add a few more sentences, please."
        : "";
  };

  // Derived state (like computed React value)
  const isSubmitButtonActive = computed<Boolean>(() => {
    return (
      name.value.length &&
      !nameError.value &&
      email.value.length &&
      !emailError.value &&
      phoneNumber.value.length &&
      !phoneNumberError.value &&
      !websiteError.value &&
      message.value.length &&
      !messageError.value &&
      contactChannel.value
    );
  });

  // Handlers
  const onSubmit = (event, ...rest) => {
    event.preventDefault();
    if (!isSubmitButtonActive.value) {
      validateName();
      validateEmail();
      validatePhoneNumber();
      validateMessage();
      return false;
    }

    // submitForm(
    //   {
    //     name: name.value,
    //     companyName: companyName.value,
    //     email: email.value,
    //     phoneNumber: phoneNumber.value,
    //     website: website.value,
    //     contactChannel: contactChannel.value,
    //     message: message.value,
    //   },
    //   ...rest
    // );
  };

  const onFormClose = () => {
    if (!name.value.length) nameError.value = "";
    if (!email.value.length) emailError.value = "";
    if (!message.value.length) messageError.value = "";
    if (!phoneNumber.value.length) phoneNumberError.value = "";
  };

  return {
    nameField: {
      value: name,
      setValue: (v) => (name.value = v),
      validate: validateName,
      errorText: nameError,
    },
    companyField: {
      value: companyName,
      setValue: (v) => (companyName.value = v),
    },
    emailField: {
      value: email,
      setValue: (v) => (email.value = v),
      validate: validateEmail,
      errorText: emailError,
    },
    phoneField: {
      value: phoneNumber,
      setValue: handlePhoneChange,
      validate: validatePhoneNumber,
      errorText: phoneNumberError,
    },
    websiteField: {
      value: website,
      setValue: (v) => (website.value = v),
      validate: validateWebsite,
      errorText: websiteError,
    },
    contactChannelField: {
      value: contactChannel,
      setValue: (v) => (contactChannel.value = v),
    },
    messageField: {
      value: message,
      setValue: (v) => (message.value = v),
      validate: validateMessage,
      errorText: messageError,
    },
    isSubmitButtonActive,
    onSubmit,
    onFormClose,
  };
}
